import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { terminBestaetingApi } from '../api/systemSettingsApi';

export default function TerminBestaetigung() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('PENDING');
  const [processing, setProcessing] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadItems();
  }, [filter]);

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await terminBestaetingApi.getAll(filter);
      setItems(data);
    } catch (error) {
      console.error('Fehler beim Laden:', error);
      setMessage({ type: 'error', text: 'Fehler beim Laden' });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      setProcessing(id);
      const result = await terminBestaetingApi.approve(id);
      setMessage({ type: 'success', text: result.message || 'Termin genehmigt' });
      loadItems();
    } catch (error) {
      console.error('Fehler:', error);
      setMessage({ type: 'error', text: 'Fehler beim Genehmigen' });
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async () => {
    if (!showRejectModal) return;
    try {
      setProcessing(showRejectModal);
      await terminBestaetingApi.reject(showRejectModal, rejectReason);
      setMessage({ type: 'success', text: 'Termin-Anfrage abgelehnt' });
      setShowRejectModal(null);
      setRejectReason('');
      loadItems();
    } catch (error) {
      console.error('Fehler:', error);
      setMessage({ type: 'error', text: 'Fehler beim Ablehnen' });
    } finally {
      setProcessing(null);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const statusBadge = (status) => {
    const styles = {
      PENDING: 'bg-amber-100 text-amber-800',
      APPROVED: 'bg-green-100 text-green-800',
      REJECTED: 'bg-red-100 text-red-800'
    };
    const labels = {
      PENDING: 'Ausstehend',
      APPROVED: 'Genehmigt',
      REJECTED: 'Abgelehnt'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const terminTypBadge = (typ) => {
    const styles = {
      ERSTELLEN: 'bg-green-100 text-green-800',
      AENDERN: 'bg-blue-100 text-blue-800',
      STORNIEREN: 'bg-red-100 text-red-800'
    };
    const icons = {
      ERSTELLEN: '➕',
      AENDERN: '✏️',
      STORNIEREN: '❌'
    };
    const labels = {
      ERSTELLEN: 'Neuer Termin',
      AENDERN: 'Terminänderung',
      STORNIEREN: 'Stornierung'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles[typ] || 'bg-gray-100'}`}>
        {icons[typ]} {labels[typ] || typ}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Termin-Genehmigungen</h1>
            <p className="text-gray-500 mt-1">
              Vom KI-Agent angeforderte Terminaktionen prüfen
            </p>
          </div>
          <button
            onClick={() => navigate('/ki-agent-settings')}
            className="text-gray-600 hover:text-gray-900"
          >
            Zurück zu Einstellungen
          </button>
        </div>

        {/* Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg ${message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {message.text}
            <button onClick={() => setMessage(null)} className="float-right">×</button>
          </div>
        )}

        {/* Filter */}
        <div className="flex gap-2 mb-6">
          {['PENDING', 'APPROVED', 'REJECTED', 'alle'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${filter === f ? 'bg-teal-600 text-white' : 'bg-white text-gray-700 border hover:border-teal-500'}`}
            >
              {f === 'PENDING' ? 'Ausstehend' : f === 'APPROVED' ? 'Genehmigt' : f === 'REJECTED' ? 'Abgelehnt' : 'Alle'}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border p-12 text-center">
            <div className="text-4xl mb-4">📅</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Keine Termin-Genehmigungen</h3>
            <p className="text-gray-500">
              {filter === 'PENDING' ? 'Keine ausstehenden Terminanfragen.' : 'Keine Einträge in dieser Kategorie.'}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((item) => (
              <div key={item.id} className="bg-white rounded-xl shadow-sm border p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex items-center gap-3">
                    {terminTypBadge(item.terminTyp)}
                    {statusBadge(item.status)}
                  </div>
                  <div className="text-sm text-gray-500">
                    Erstellt: {formatDateTime(item.createdAt)}
                  </div>
                </div>

                {/* Termin-Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {item.kunde?.name || 'Unbekannter Kunde'}
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500">📅 Datum:</span>
                        <span className="font-medium">{formatDate(item.datum)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500">🕐 Uhrzeit:</span>
                        <span className="font-medium">{item.uhrzeit} Uhr</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-500">⏱️ Dauer:</span>
                        <span className="font-medium">{item.dauer} Minuten</span>
                      </div>
                    </div>
                  </div>

                  {/* Alter Termin bei Änderung */}
                  {item.terminTyp === 'AENDERN' && item.alterTermin && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Aktueller Termin:</h4>
                      <div className="text-sm text-gray-600">
                        <div>{formatDate(item.alterTermin.datum)}</div>
                        <div>{item.alterTermin.uhrzeit} Uhr</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Beschreibung */}
                {item.beschreibung && (
                  <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Beschreibung:</h4>
                    <p className="text-gray-600 text-sm">{item.beschreibung}</p>
                  </div>
                )}

                {/* KI-Begründung */}
                <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                  <h4 className="text-sm font-medium text-blue-800 mb-2">KI-Begründung:</h4>
                  <p className="text-blue-700 text-sm">{item.kiBegruendung}</p>
                </div>

                {/* Email-Kontext */}
                {item.emailKontext && (
                  <details className="mb-4">
                    <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                      Original E-Mail-Kontext anzeigen
                    </summary>
                    <div className="mt-2 p-4 bg-gray-100 rounded-lg text-sm text-gray-600 whitespace-pre-wrap">
                      {item.emailKontext}
                    </div>
                  </details>
                )}

                {/* Aktionen */}
                {item.status === 'PENDING' && (
                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <button
                      onClick={() => setShowRejectModal(item.id)}
                      disabled={processing === item.id}
                      className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
                    >
                      Ablehnen
                    </button>
                    <button
                      onClick={() => handleApprove(item.id)}
                      disabled={processing === item.id}
                      className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
                    >
                      {processing === item.id ? 'Wird verarbeitet...' : (
                        item.terminTyp === 'ERSTELLEN' ? 'Genehmigen & Termin erstellen' :
                        item.terminTyp === 'AENDERN' ? 'Genehmigen & Termin ändern' :
                        'Genehmigen & Stornieren'
                      )}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reject Modal */}
      <AnimatePresence>
        {showRejectModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <motion.div
              className="fixed inset-0 bg-black/50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              onClick={() => { setShowRejectModal(null); setRejectReason(''); }}
            />
            <motion.div
              className="bg-white rounded-xl shadow-lg max-w-md w-full p-6 relative z-10"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{
                duration: 0.15,
                ease: "easeOut",
                exit: { duration: 0.1, ease: "easeIn" }
              }}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Termin-Anfrage ablehnen</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Grund für die Ablehnung (optional):
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={3}
                  className="w-full border rounded-lg px-4 py-2 focus:ring-2 focus:ring-teal-500"
                  placeholder="Warum wird diese Anfrage abgelehnt?"
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => { setShowRejectModal(null); setRejectReason(''); }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleReject}
                  disabled={processing}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {processing ? 'Wird abgelehnt...' : 'Ablehnen'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
